/* @sravni/react-design-system version 0.2.65 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { useStyles, useFormControlContext, useBoolean, useId, isUndefined, safeInvoke, useMergedRef } from '@sravni/react-utils';
import '../../Icon/index.js';
import '../../Spinner/index.js';
import modules_56feba8c from '../styles/TextInput.module.less.js';
import Spinner from '../../Spinner/src/Spinner.js';
import Icon from '../../Icon/src/Icon.js';

function fixControlledValue(value) {
    if (typeof value === 'undefined' || value === null) {
        return '';
    }
    return value;
}
/**
 * @description The Text Input component allows user to type in text
 *
 * @component
 * @example
 * ```jsx
 * <TextInput name="field" value="value" />
 * ```
 */
var TextInput = React.forwardRef(function (props, ref) {
    var _a;
    var id = props.id, autoFocus = props.autoFocus, defaultValue = props.defaultValue, value = props.value, label = props.label, placeholder = props.placeholder, postfix = props.postfix, icon = props.icon, leftIcon = props.leftIcon, disabled = props.disabled, invalid = props.invalid, readOnly = props.readOnly, loading = props.loading, positionInGroup = props.positionInGroup, directionGroup = props.directionGroup, background = props.background, className = props.className, type = props.type, onFocus = props.onFocus, onBlur = props.onBlur, onChange = props.onChange, onClick = props.onClick, restProps = __rest(props, ["id", "autoFocus", "defaultValue", "value", "label", "placeholder", "postfix", "icon", "leftIcon", "disabled", "invalid", "readOnly", "loading", "positionInGroup", "directionGroup", "background", "className", "type", "onFocus", "onBlur", "onChange", "onClick"]);
    var cx = useStyles(modules_56feba8c);
    var formControlContext = useFormControlContext();
    var containerRef = React.useRef(null);
    var inputSegmentRef = React.useRef(null);
    var inputRef = React.useRef();
    var _b = __read(useBoolean(false), 2), focused = _b[0], setFocused = _b[1];
    var _c = __read(useBoolean(value !== undefined && value !== ''), 2), filled = _c[0], setFilled = _c[1];
    var inputId = id !== null && id !== void 0 ? id : useId('textInput');
    var isActive = focused && !readOnly;
    var isDisabled = formControlContext.disabled || disabled;
    var isInvalid = formControlContext.invalid || invalid;
    var hasIcon = icon || loading;
    var onlyPlaceholder = !!placeholder && !label;
    var classNames = cx(className, 'container', (_a = {
            focused: focused,
            active: isActive,
            disabled: isDisabled,
            invalid: isInvalid,
            withIcon: hasIcon
        },
        _a["background-" + background] = background,
        _a["group-" + directionGroup] = directionGroup,
        _a["group-" + positionInGroup] = positionInGroup,
        _a));
    var classNamesLabel = cx('label', {
        active: isActive,
        filled: filled,
        disabled: isDisabled,
        invalid: isInvalid,
        withIcon: hasIcon,
        leftMargin: !!leftIcon,
    });
    var classNamesPlaceholder = cx('placeholder', {
        active: isActive,
        filled: filled,
        disabled: isDisabled,
        invalid: isInvalid,
        withIcon: hasIcon,
        withLabel: label && focused,
        hidden: label && !focused,
        leftMargin: !!leftIcon,
    });
    var classNamesInput = cx('input', {
        active: isActive,
        filled: filled,
        disabled: isDisabled,
        invalid: isInvalid,
        hidden: !readOnly && !focused,
    });
    var classNamesInputSegment = cx('inputSegment', {
        centered: isUndefined(label) && (filled || focused),
        onlyPlaceholder: onlyPlaceholder,
        leftMargin: !!leftIcon,
        withIcon: hasIcon,
    });
    var iconElement = React.useMemo(function () {
        if (loading) {
            return React.createElement(Spinner, { className: cx('icon') });
        }
        if (icon) {
            return React.createElement(Icon, { className: cx('icon'), icon: icon });
        }
        return null;
    }, [icon, loading]);
    var postfixElement = React.useMemo(function () {
        if (postfix && filled) {
            return React.createElement("div", { className: cx('inputPostfix') },
                "\u00A0",
                postfix);
        }
        return null;
    }, [postfix, filled]);
    var labelElement = React.useMemo(function () {
        if (label) {
            return (React.createElement("label", { htmlFor: inputId, className: classNamesLabel }, label));
        }
        return null;
    }, [inputId, label, classNamesLabel]);
    var placeholderElement = React.useMemo(function () {
        if ((label && readOnly) || filled) {
            return null;
        }
        if (placeholder) {
            return (React.createElement("label", { htmlFor: inputId, className: classNamesPlaceholder }, placeholder));
        }
        return null;
    }, [inputId, label, placeholder, filled, readOnly, classNamesPlaceholder]);
    var handleContainerClick = function (event) {
        var _a;
        if (disabled) {
            return;
        }
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        setFocused.on();
        //TODO: Might be worth redefining the onClick event to React.MouseEvent<HtmlDivElement>
        safeInvoke(onClick, event);
    };
    var handleContainerBlur = function (event) {
        var _a;
        //needs to prevent extra onBlur callback executing on TextInput wrapper
        var relTarget = event.relatedTarget;
        if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(relTarget)) {
            event.stopPropagation();
        }
    };
    var handleContainerFocus = function (event) {
        //needs to prevent extra onFocus callback executing on TextInput wrapper
        var relTarget = event.relatedTarget;
        if (relTarget === inputSegmentRef.current || relTarget === containerRef.current) {
            event.stopPropagation();
        }
    };
    var handleFocus = React.useCallback(function (event) {
        if (!focused) {
            setFocused.on();
            safeInvoke(onFocus, event);
        }
    }, [focused, onFocus]);
    var handleBlur = React.useCallback(function (event) {
        var relTarget = event.relatedTarget;
        if (relTarget === inputSegmentRef.current || relTarget === containerRef.current)
            return;
        setFocused.off();
        safeInvoke(onBlur, event);
    }, [onBlur]);
    var handleChange = React.useCallback(function (event) {
        safeInvoke(onChange, event);
    }, [onChange]);
    React.useEffect(function () {
        if (value !== undefined && value !== '') {
            setFilled.on();
        }
        else {
            setFilled.off();
        }
    }, [value]);
    return (React.createElement("div", { "data-qa": "TextInput", ref: containerRef, className: classNames, tabIndex: -1, onClick: handleContainerClick, onBlur: handleContainerBlur, onFocus: handleContainerFocus },
        leftIcon && React.createElement(Icon, { className: cx('icon', 'left'), icon: leftIcon }),
        React.createElement("div", { ref: inputSegmentRef, className: classNamesInputSegment, tabIndex: -1 },
            React.createElement("div", { className: cx('inputWrapper') },
                postfix && React.createElement("div", { className: cx('inputValue') }, fixControlledValue(value)),
                React.createElement("input", __assign({ autoFocus: autoFocus, spellCheck: "false", autoComplete: "off", id: inputId, ref: useMergedRef(inputRef, ref), type: type, value: fixControlledValue(value), disabled: isDisabled, readOnly: readOnly, className: classNamesInput, onFocus: handleFocus, onBlur: handleBlur, onChange: handleChange }, restProps)),
                postfixElement)),
        labelElement,
        placeholderElement,
        iconElement));
});
TextInput.defaultProps = {
    type: 'text',
};
TextInput.displayName = 'TextInput';

export default TextInput;
export { fixControlledValue };
//# sourceMappingURL=TextInput.js.map
